import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { fetchArtistOptions } from '../../shared/components/ArtistSelectComponents';
import V3NewArtistPanel from './V3NewArtistPanel';
import '../styles/artistPicker.css';

const normalizeName = (s) => (s || '').trim().toLowerCase().replace(/[.\s]+$/, '');

const initials = (name) => {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0] || '')
    .join('')
    .toUpperCase();
};

// Shared artist picker body — search an existing artist or create one inline.
// The universal picker: rendered in the mobile bottom sheet (variant="sheet")
// and, as variant="modal", by the V3 offer change/add-artist popups, the Event
// Page lineup card, and every ArtistPickerField consumer (follow-ups, outreach,
// My Artists, ConfirmTicketing, the Simple deals form). Owns the search +
// selection state; submission is delegated to onSubmit, which receives
// { artistId, newArtistName, actType, spotifyId, externalUrl, artist,
// spotifyTakeover } — exactly one of artistId/newArtistName is set, and
// actType is only present in add-sibling mode. spotifyId/externalUrl/
// spotifyTakeover are only populated when the Spotify create panel (teamId
// present) is what produced the submission.
//
// teamId is optional and gates the Spotify create panel: when present, the
// "create new artist" action opens V3NewArtistPanel; when absent, it falls
// back to the original immediate name-only create. allowCreate=false hides
// the create path entirely, for pick-existing-only contexts (follow-ups,
// outreach, attach-to-offer). fetchOptions lets a caller swap the search
// source; it must match fetchArtistOptions' (query, callback) contract.
const V3PerformerPicker = ({
  currentPerformer, mode, onSubmit, onClose, variant, description, defaultActType, teamId,
  allowCreate, fetchOptions, allowTba,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [actType, setActType] = useState(defaultActType);
  const [submitting, setSubmitting] = useState(false);
  const [creating, setCreating] = useState(null);
  const inputRef = useRef(null);

  // The picker only mounts while open (reactjs-popup unmounts its content on
  // close), so a one-time mount reset gives every open a clean slate. Focus the
  // search input on that same mount so a keyboard user can type immediately —
  // the autoFocus attribute is unreliable inside the popup's portal.
  useEffect(() => {
    setQuery('');
    setResults([]);
    setActType(defaultActType);
    setSubmitting(false);
    setCreating(null);
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!query) { setResults([]); return undefined; }
    let ignore = false;
    fetchOptions(query, (opts) => {
      if (!ignore) setResults(opts || []);
    });
    return () => { ignore = true; };
  }, [query, fetchOptions]);

  const handleSelect = useCallback(async (option) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({
        artistId: option.value,
        newArtistName: null,
        actType: mode === 'add-sibling' ? actType : undefined,
        artist: option.object?.artistable || null,
      });
    } finally {
      setSubmitting(false);
    }
  }, [submitting, onSubmit, actType, mode]);

  const handleCreate = useCallback(async () => {
    const name = query.trim();
    if (!name || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({
        artistId: null,
        newArtistName: name,
        actType: mode === 'add-sibling' ? actType : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  }, [query, submitting, onSubmit, actType, mode]);

  const handleTba = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({ artistId: null, newArtistName: null, actType: 'support', placeholder: true });
    } finally {
      setSubmitting(false);
    }
  }, [submitting, onSubmit]);

  const handlePanelSubmit = useCallback(async (sel) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({
        artistId: sel.artistId || null,
        newArtistName: sel.newArtistName || null,
        actType: sel.actType,
        spotifyId: sel.spotifyId || null,
        externalUrl: sel.externalUrl || null,
        artist: sel.artist || null,
        spotifyTakeover: sel.spotifyTakeover || false,
      });
    } finally {
      setSubmitting(false);
    }
  }, [submitting, onSubmit]);

  const showCurrent = mode === 'edit' && currentPerformer;
  const performerName = currentPerformer?.artistable?.name
    || currentPerformer?.name
    || '';
  const performerImageUrl = currentPerformer?.imageUrl
    || currentPerformer?.artistable?.imageUrl
    || '';
  const performerRole = (() => {
    const t = currentPerformer?.act_type;
    if (t === 'headline') return 'Headliner';
    if (t === 'support') return 'Support';
    return '';
  })();

  const title = mode === 'edit' && currentPerformer ? 'Change artist' : 'Add artist';
  const desc = description || (mode === 'edit'
    ? 'Replace the artist on this offer with a different one, or add a new one inline.'
    : 'Search any artist on Opendate, or add a new one inline.');

  const exactNameMatches = results.filter((r) => normalizeName(r.label) === normalizeName(query));
  const onlyDisabledExactMatch = exactNameMatches.length > 0 && exactNameMatches.every((r) => r.disabled);
  const pickableInRosterExactMatch = exactNameMatches.some((r) => r.inRoster && !r.disabled);
  const showCreatable = allowCreate && !!query.trim() && !onlyDisabledExactMatch && !pickableInRosterExactMatch;
  const showEmptyHint = !query.trim();

  return (
    <div
      className={`add-artist-modal${variant === 'modal' ? ' add-artist-modal--modal' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`performer-picker-title-${variant}`}
      data-testid="performer-picker-sheet"
    >
      <header className="add-artist-modal__header">
        <div>
          <h2 className="add-artist-modal__title" id={`performer-picker-title-${variant}`}>{title}</h2>
          <p className="add-artist-modal__desc">{desc}</p>
        </div>
        <button
          type="button"
          className="add-artist-modal__close"
          aria-label="Close"
          onClick={onClose}
        >
          <i className="far fa-times" />
        </button>
      </header>

      <div className="add-artist-modal__body">
        {showCurrent && (
          <div className="add-artist-current" data-testid="performer-picker-current">
            <span className="add-artist-current__label">Currently set</span>
            <div className="add-artist-current__card">
              {performerImageUrl ? (
                <img className="add-artist-current__avatar" src={performerImageUrl} alt="" />
              ) : (
                <span className="add-artist-current__avatar">{initials(performerName)}</span>
              )}
              <span className="add-artist-current__name">{performerName}</span>
              {performerRole && (
                <span className="add-artist-current__meta">{performerRole}</span>
              )}
            </div>
          </div>
        )}

        {mode === 'add-sibling' && (
          <div
            className="add-artist-role"
            role="radiogroup"
            aria-label="Role"
            data-testid="performer-picker-role-toggle"
          >
            <button
              type="button"
              className={`add-artist-role__btn${actType === 'headline' ? ' add-artist-role__btn--active' : ''}`}
              onClick={() => setActType('headline')}
              aria-pressed={actType === 'headline'}
            >
              Headliner
            </button>
            <button
              type="button"
              className={`add-artist-role__btn${actType === 'support' ? ' add-artist-role__btn--active' : ''}`}
              onClick={() => setActType('support')}
              aria-pressed={actType === 'support'}
            >
              Support
            </button>
          </div>
        )}

        {creating && teamId ? (
          <V3NewArtistPanel
            teamId={teamId}
            initialName={creating}
            actType={actType}
            submitting={submitting}
            onBack={() => setCreating(null)}
            onSubmit={handlePanelSubmit}
          />
        ) : (
          <>
            <div className="add-artist-input">
              <span className="add-artist-input__leading" aria-hidden="true">
                <i className="far fa-search" />
              </span>
              <input
                ref={inputRef}
                type="text"
                className="add-artist-input__field"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search artists…"
                autoComplete="off"
                data-testid="performer-picker-input"
                disabled={submitting}
              />
            </div>

            <div className="add-artist-menu" data-testid="performer-picker-results">
              {results.map((opt) => {
                const spotifyLinked = opt.object?.artistable?.spotify_linked;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className="add-artist-result"
                    // aria-disabled rather than disabled: an unavailable row
                    // stays focusable so keyboard/SR users can reach its badge
                    // and learn why it can't be picked.
                    onClick={() => { if (!opt.disabled) handleSelect(opt); }}
                    aria-disabled={opt.disabled || undefined}
                    disabled={submitting}
                    data-testid="performer-picker-result"
                  >
                    {opt.imageUrl ? (
                      <img className="add-artist-result__avatar" src={opt.imageUrl} alt="" />
                    ) : (
                      <span className="add-artist-result__avatar">{initials(opt.label)}</span>
                    )}
                    <span className="add-artist-result__name">{opt.label}</span>
                    {opt.badge ? (
                      <span className="add-artist-result__chip" data-testid="performer-picker-badge">{opt.badge}</span>
                    ) : opt.inRoster ? (
                      <span className="add-artist-result__chip" data-testid="performer-picker-roster-chip">Your roster</span>
                    ) : (
                      <>
                        {spotifyLinked === true && (
                          <i className="fab fa-spotify" data-testid="performer-picker-spotify-icon" />
                        )}
                        {spotifyLinked === false && (
                          <span className="add-artist-result__chip">No Spotify</span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}

              {pickableInRosterExactMatch && (
                <>
                  {results.length > 0 && <div className="add-artist-divider" />}
                  <div className="add-artist-hint" data-testid="performer-picker-dup-hint">
                    You already work with an artist named &ldquo;{query.trim()}&rdquo; — pick it above.
                  </div>
                </>
              )}
              {showCreatable && (
                <>
                  {results.length > 0 && <div className="add-artist-divider" />}
                  <button
                    type="button"
                    className="add-artist-create"
                    onClick={() => {
                      if (teamId) {
                        setCreating(query.trim());
                      } else {
                        handleCreate();
                      }
                    }}
                    disabled={submitting}
                    data-testid="performer-picker-create"
                  >
                    <span className="add-artist-create__icon" aria-hidden="true">
                      <i className="far fa-plus" />
                    </span>
                    <span className="add-artist-create__text">
                      <span>Add &ldquo;{query.trim()}&rdquo; as a new artist</span>
                      <small>We&rsquo;ll look them up on Spotify next</small>
                    </span>
                  </button>
                </>
              )}

              {showEmptyHint && (
                <div className="add-artist-empty" data-testid="performer-picker-empty">
                  Start typing to find an artist…
                </div>
              )}

              {allowTba && mode === 'add-sibling' && actType === 'support' && (
                <>
                  <div className="add-artist-divider" />
                  <button
                    type="button"
                    className="add-artist-create"
                    onClick={handleTba}
                    disabled={submitting}
                    data-testid="performer-picker-tba"
                  >
                    <span className="add-artist-create__icon" aria-hidden="true">
                      <i className="far fa-question" />
                    </span>
                    <span className="add-artist-create__text">
                      <span>Artist TBA — add as TBA Support</span>
                      <small>Budget the deal now and pick the artist later</small>
                    </span>
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

V3PerformerPicker.propTypes = {
  currentPerformer: PropTypes.shape({
    id: PropTypes.number,
    act_type: PropTypes.string,
    name: PropTypes.string,
    artistable: PropTypes.shape({ name: PropTypes.string }),
  }),
  mode: PropTypes.oneOf(['edit', 'add-sibling']).isRequired,
  allowTba: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['sheet', 'modal']),
  description: PropTypes.string,
  defaultActType: PropTypes.oneOf(['headline', 'support']),
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  allowCreate: PropTypes.bool,
  fetchOptions: PropTypes.func,
};

V3PerformerPicker.defaultProps = {
  currentPerformer: null,
  variant: 'sheet',
  description: null,
  defaultActType: 'support',
  teamId: null,
  allowCreate: true,
  fetchOptions: fetchArtistOptions,
  allowTba: false,
};

export default V3PerformerPicker;
